import React, {useEffect, useState} from "react";
import {
    Avatar, Box,
    Button,
    Card, Chip,
    FormControl,
    FormHelperText,
    FormLabel,
    Grid,
    Input, Radio, radioClasses,
    RadioGroup,
    Stack,
    Typography, useTheme
} from "@mui/joy";
import {Camera, CheckCircle2, LucideMail, UserIcon} from "lucide-react";
import {UserFormData} from "../../models/user";
import {useSettings} from "../../hooks/useSettings";
import {EXAM_TYPE_MAPPER, ExamType, LANGUAGE_TIERS, LanguageTier} from "../../models/exam";
import {useTranslation} from "react-i18next";
import {SUPPORTED_REGIONS} from "../../models/region";


const AccountSettingsSection = () => {
    const settings = useSettings()
    const theme = useTheme()
    const [languageTier, setLanguageTier] = useState<LanguageTier>()
    const {t} = useTranslation()
    const examFieldScoreMapper: { [key in ExamType]?: string } = {
        tcf_ca: "clb",
        ielts: "band",
        tcf_fr: "points",
        toefl: "score",
    }

    const fieldValidation: {
        [key in keyof UserFormData]: {
            required: boolean,
            message?: string,
            check?: boolean
        }
    } = {
        name: {
            check: new RegExp(/^[A-Za-zÀ-ÖØ-öø-ÿ' -]{2,}$/).test(settings.userFormData?.name),
            message: t("invalidNameFieldMessage"),
            required: true
        },
        email: {
            required: true,
            message: t("invalidEmailFieldMessage"),
            check: new RegExp(/^[^\s@]+@[^\s@]+\.[^\s@]+$/).test(settings.userFormData?.email)
        }
    }

    const handleAvatarChange = (files: FileList) => {
        const reader = new FileReader()
        reader.readAsDataURL(files[0])
        reader.onload = () => {
            settings.onUserFormDataChange("avatar", reader.result as string)
        }

        reader.onerror = (error) => {
            console.error("Error converting image:", error);
        }
    }

    useEffect(() => {
        if (languageTier)
            settings.onUserFormDataChange("targetScore", languageTier[examFieldScoreMapper[settings.userFormData?.preferredExam]])
    }, [languageTier])


    return <Stack gap={2}>
        <Card data-no-glow data-no-animation sx={{padding: 3}}>
            <Typography>
                {t("personalInfoFormTitle")}
            </Typography>

            <Stack gap={4} alignItems={"center"} direction={"row"}>

                <Box position={"relative"}>
                    <Avatar variant={"solid"} src={settings.userFormData?.avatar} color={"primary"} size={"lg"}
                            sx={{'--Avatar-size': "72px"}}>
                        <UserIcon style={{fontSize: 44}}/>
                    </Avatar>
                    <Avatar variant={"solid"}
                            sx={{backgroundColor: "skyblue", position: "absolute", bottom: -5, right: -5}}
                            size={"sm"}>
                        <Camera/>
                    </Avatar>
                </Box>


                <Stack gap={1}>
                    <Typography level={"title-md"}>
                        {t("updateProfilePictureTitle")}
                    </Typography>
                    <Typography>
                        {t("updateProfilePictureSupportedFileText")}
                    </Typography>


                    <label htmlFor={"avatar"}>
                        <Chip color={"primary"} sx={{cursor: "pointer"}} size={"lg"} variant={"outlined"}
                              onChange={() => {
                              }}>
                            {t("updateProfilePictureActionLabel")}
                        </Chip>

                        <input id={"avatar"} type={"file"} accept={"image/*"}
                               style={{display: "none"}}
                               onChange={e => {
                                   e.preventDefault()
                                   handleAvatarChange(e.target.files)
                               }}/>
                    </label>

                </Stack>

            </Stack>

        </Card>

        <Card data-no-glow data-no-animation sx={{padding: 3}}>
            <Typography level={'title-md'}>
                {t("personalInfoFormTitle")}
            </Typography>

            <FormControl error={settings.userFormData.name && !fieldValidation.name.check}>
                <FormLabel>
                    <Typography level={"body-sm"}>
                        {t("fullNameFieldLabel")}
                    </Typography>
                </FormLabel>
                <Input size={"lg"}
                       value={settings.userFormData.name}
                       onChange={(event) => settings.onUserFormDataChange("name", event.target.value)}
                       placeholder={t("fullNameFieldPlaceholder")} name={"name"}/>

                {(settings.userFormData.name && !fieldValidation.name.check) &&
                    <FormHelperText>
                        {fieldValidation.name.message}
                    </FormHelperText>}
            </FormControl>

            <FormControl error={settings.userFormData.email && !fieldValidation.email.check}>
                <FormLabel>
                    <Typography level={"body-sm"}>
                        {t("emailFieldLabel")}
                    </Typography>
                </FormLabel>
                <Input size={"lg"}
                       value={settings.userFormData.email}
                       onChange={(event) => settings.onUserFormDataChange("email", event.target.value)}
                       placeholder={t("emailFieldPlaceholder")} type={"email"}/>

                {(settings.userFormData.email && !fieldValidation.email.check) &&
                    <FormHelperText>
                        {fieldValidation.email.message}
                    </FormHelperText>}
            </FormControl>

            <FormControl error={settings.userFormData.email && !fieldValidation.email.check}>
                <FormLabel>
                    <Typography level={"body-sm"}>
                        {t("preferredLanguageLabel")}
                    </Typography>
                </FormLabel>

                <RadioGroup
                    aria-label="platform"
                    overlay
                    value={settings.userFormData.language}
                    onChange={(event) => settings.onUserFormDataChange("language", event.target.value)}
                    sx={{
                        flexDirection: 'row',
                        gap: 2,
                        [`& .${radioClasses.checked}`]: {
                            [`& .${radioClasses.action}`]: {
                                inset: -1,
                            },
                        },
                        [`& .${radioClasses.radio}`]: {
                            display: 'contents',
                            '& > svg': {
                                zIndex: 2,
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                bgcolor: 'background.surface',
                                borderRadius: '50%',
                            },
                        },
                    }}>
                    <Grid container spacing={2}>

                        <Grid xs={12} md={6}>
                            <Card sx={{gap: 1}} data-no-animation data-no-glow>
                                <Radio color={"success"} id={"en"} value={"en"}
                                       checkedIcon={<CheckCircle2
                                           style={{fontSize: 30, color: theme.palette.success['400']}}/>}/>
                                <Typography startDecorator={<Avatar size={"sm"} src={require("../../assets/en.png")}/>}
                                            textTransform={"capitalize"} level={"title-md"}>
                                    {t("preferredLanguageENOptionLabel")}
                                </Typography>
                                <Typography level={"body-sm"}>
                                    {t("preferredLanguageENOptionDescription")}
                                </Typography>
                            </Card>
                        </Grid>

                        <Grid xs={12} md={6}>
                            <Card sx={{gap: 1}} data-no-animation data-no-glow>
                                <Radio color={"success"} id={"fr"} value={"fr"}
                                       checkedIcon={<CheckCircle2
                                           style={{fontSize: 30, color: theme.palette.success['400']}}/>}/>
                                <Typography startDecorator={<Avatar size={"sm"} src={require("../../assets/fr.webp")}/>}
                                            textTransform={"capitalize"} level={"title-md"}>
                                    {t("preferredLanguageFROptionLabel")}
                                </Typography>
                                <Typography level={"body-sm"}>
                                    {t("preferredLanguageFROptionDescription")}
                                </Typography>
                            </Card>
                        </Grid>

                    </Grid>

                </RadioGroup>

                {(settings.userFormData.email && !fieldValidation.email.check) &&
                    <FormHelperText>
                        {fieldValidation.email.message}
                    </FormHelperText>}
            </FormControl>

            <FormControl>
                <FormLabel>
                    <Typography level={"body-sm"}>
                        {t("preferredExamFieldLabel")}
                    </Typography>
                </FormLabel>

                <RadioGroup
                    aria-label="platform"
                    overlay
                    value={settings.userFormData.preferredExam}
                    onChange={(event) => settings.onUserFormDataChange("preferredExam", event.target.value)}
                    sx={{
                        flexDirection: 'row',
                        gap: 2,
                        [`& .${radioClasses.checked}`]: {
                            [`& .${radioClasses.action}`]: {
                                inset: -1,
                            },
                        },
                        [`& .${radioClasses.radio}`]: {
                            display: 'contents',
                            '& > svg': {
                                zIndex: 2,
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                bgcolor: 'background.surface',
                                borderRadius: '50%',
                            },
                        },
                    }}>
                    <Grid container spacing={2}>
                        {EXAM_TYPE_MAPPER.map((exam) => (
                            <Grid sm={6} xs={12}>
                                <Card sx={{gap: 1, opacity: exam.available ? 1 : 0.5}} data-no-animation data-no-glow>

                                    {!exam.available &&
                                        <Chip color={"secondary"} size={"sm"}
                                              sx={{alignSelf: "end", position: "absolute", right: 10}}>
                                            {t("examSoonAvailableLabel")}
                                        </Chip>}

                                    <Radio color={"success"} disabled={!exam.available} id={exam.value}
                                           value={exam.value}
                                           checkedIcon={<CheckCircle2
                                               style={{fontSize: 30, color: theme.palette.success['400']}}/>}/>
                                    <Typography textTransform={"uppercase"} level={"title-md"}>
                                        {exam.label}
                                    </Typography>
                                    <Typography level={"body-sm"}>
                                        {exam.description}
                                    </Typography>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                </RadioGroup>
            </FormControl>

            {settings.userFormData?.preferredExam &&
                <FormControl>
                    <FormLabel>
                        <Typography level={"body-sm"}>
                            {t("targetScoreFieldLabel")}
                        </Typography>
                    </FormLabel>

                    <RadioGroup
                        aria-label="platform"
                        defaultValue="Website"
                        overlay
                        value={languageTier?.id}
                        onChange={event =>
                            setLanguageTier(LANGUAGE_TIERS[settings.userFormData?.preferredExam]?.tiers.find(value => value.id === event.target.value))}
                        sx={{
                            flexDirection: 'row',
                            gap: 2,
                            [`& .${radioClasses.checked}`]: {
                                [`& .${radioClasses.action}`]: {
                                    inset: -1,
                                },
                            },
                            [`& .${radioClasses.radio}`]: {
                                display: 'contents',
                                '& > svg': {
                                    zIndex: 2,
                                    position: 'absolute',
                                    top: '10px',
                                    right: '10px',
                                    bgcolor: 'background.surface',
                                    borderRadius: '50%',
                                },
                            },
                        }}>

                        <Grid container spacing={2}>
                            {LANGUAGE_TIERS[settings.userFormData?.preferredExam]?.tiers.map((value) => (
                                <Grid sm={6} xs={12}>
                                    <Card sx={{gap: 1}} data-no-animation data-no-glow>
                                        <Radio color={"success"} id={value.id} value={value.id}
                                               checkedIcon={<CheckCircle2
                                                   style={{fontSize: 30, color: theme.palette.success['400']}}/>}/>
                                        {settings.userFormData.preferredExam === "tcf_fr" ?
                                            <Typography textTransform={"uppercase"} level={"title-md"}>
                                                {t(value.label)} ({value.cefr[0]})
                                            </Typography> :
                                            <Typography textTransform={"uppercase"} level={"title-md"}>
                                                {t(value.label)} ({value[examFieldScoreMapper[settings.userFormData?.preferredExam]].min} - {value[examFieldScoreMapper[settings.userFormData?.preferredExam]].max})
                                            </Typography>}
                                        <Typography level={"body-sm"}>
                                            {t(value.description)}
                                        </Typography>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>

                    </RadioGroup>

                </FormControl>}
        </Card>

    </Stack>
}

export default AccountSettingsSection